<template>
  <div class="protest-filter-panel">
    <!-- Filters Stack (vertical layout for narrow panel) -->
    <div class="filters-stack">
      <!-- Date Range Filter -->
      <div class="filter-group">
        <label class="filter-label">Date Range</label>
        <ThemedSelect
          v-model="selectedDateRange"
          :options="dateRangeOptions"
          @input="handleDateRangeChange"
        />
      </div>
      
      <!-- Custom Date Inputs (shown when Custom is selected) -->
      <div v-if="selectedDateRange === 'custom'" class="filter-group">
        <label class="filter-label">Start Date</label>
        <ThemedInput
          v-model="localFilters.dateRange.startDate"
          @input="emitFilters"
          type="date"
        />
        <label class="filter-label" style="margin-top: 0.5rem;">End Date</label>
        <ThemedInput
          v-model="localFilters.dateRange.endDate"
          @input="emitFilters"
          type="date"
        />
      </div>
      
      <!-- Search Text Filter -->
      <div class="filter-group">
        <label class="filter-label">Search Events</label>
        <ThemedInput
          v-model="localFilters.searchText"
          @input="debouncedEmitFilters"
          type="search"
          placeholder="Search events..."
        />
      </div>
      
      <!-- Factor Filter -->
      <div class="filter-group">
        <label class="filter-label">Filter by Topics</label>
        <ThemedMultiSelect
          v-model="localFilters.selectedFactors"
          :options="categorizedFactors"
          placeholder="Select topics..."
          :grouped="true"
          @input="emitFilters"
        />
      </div>
      
      <!-- Clear Button -->
      <div class="filter-group">
        <ThemedButton variant="outline" @click="clearFilters" class="clear-filters-button w-full">
          Clear All Filters
        </ThemedButton>
      </div>
    </div>
  </div>
</template>

<script>
import ThemedSelect from '../ThemedSelect.vue';
import ThemedMultiSelect from '../ThemedMultiSelect.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
  name: 'ProtestFilterPanel',

  components: {
    ThemedSelect,
    ThemedMultiSelect,
    ThemedInput,
    ThemedButton,
  },
  
  props: {
    filters: {
      type: Object,
      default: () => ({
        dateRange: {
          startDate: '',
          endDate: ''
        },
        searchText: '',
        selectedFactors: []
      })
    },
    show: {
      type: Boolean,
      default: false
    },
    protestData: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      localFilters: {
        dateRange: {
          startDate: '',
          endDate: ''
        },
        searchText: '',
        selectedFactors: []
      },
      debounceTimeout: null,
      selectedDateRange: ''
    };
  },
  
  computed: {
    dateRangeOptions() {
      return [
        { label: 'All dates', value: '' },
        { label: 'Last 30 days', value: '30' },
        { label: 'Last 60 days', value: '60' },
        { label: 'Last 90 days', value: '90' },
        { label: 'Last 120 days', value: '120' },
        { label: 'Custom range', value: 'custom' }
      ];
    },
    
    categorizedFactors() {
      // If protest data is loaded, extract topics from it
      if (this.protestData && this.protestData.length > 0) {
        return this.extractCategorizedFactors();
      }
      // Fallback: return static category list so filter is usable before data loads
      return [
        { name: 'Geopolitical / Conflict-Related', options: [
          { value: 'gaza', label: 'Gaza' }, { value: 'palestine', label: 'Palestine' },
          { value: 'israel', label: 'Israel' }, { value: 'ceasefire', label: 'Ceasefire' },
          { value: 'war', label: 'War' }, { value: 'peace', label: 'Peace' }
        ]},
        { name: 'Immigration', options: [
          { value: 'immigration', label: 'Immigration' }, { value: 'deportation', label: 'Deportation' },
          { value: 'ice', label: 'ICE' }, { value: 'border', label: 'Border' },
          { value: 'refugee', label: 'Refugee' }, { value: 'asylum', label: 'Asylum' }
        ]},
        { name: 'Political / Government', options: [
          { value: 'trump', label: 'Trump' }, { value: 'biden', label: 'Biden' },
          { value: 'democracy', label: 'Democracy' }, { value: 'election', label: 'Election' }
        ]},
        { name: 'Climate / Environment', options: [
          { value: 'climate', label: 'Climate' }, { value: 'environment', label: 'Environment' },
          { value: 'pipeline', label: 'Pipeline' }, { value: 'energy', label: 'Energy' }
        ]},
        { name: 'Healthcare', options: [
          { value: 'healthcare', label: 'Healthcare' }, { value: 'medicare', label: 'Medicare' },
          { value: 'medicaid', label: 'Medicaid' }
        ]},
        { name: 'Workers / Labor', options: [
          { value: 'workers', label: 'Workers' }, { value: 'labor', label: 'Labor' },
          { value: 'union', label: 'Union' }, { value: 'wage', label: 'Wage' }
        ]},
        { name: 'Justice / Police', options: [
          { value: 'police', label: 'Police' }, { value: 'justice', label: 'Justice' },
          { value: 'prison', label: 'Prison' }, { value: 'reform', label: 'Reform' }
        ]},
        { name: 'Rights / Equality', options: [
          { value: 'rights', label: 'Rights' }, { value: 'lgbtq', label: 'LGBTQ' },
          { value: 'transgender', label: 'Transgender' }, { value: 'women', label: 'Women' },
          { value: 'equality', label: 'Equality' }
        ]},
        { name: 'Education', options: [
          { value: 'education', label: 'Education' }, { value: 'school', label: 'School' },
          { value: 'student', label: 'Student' }
        ]},
        { name: 'Housing', options: [
          { value: 'housing', label: 'Housing' }, { value: 'rent', label: 'Rent' },
          { value: 'eviction', label: 'Eviction' }, { value: 'homeless', label: 'Homeless' }
        ]},
        { name: 'Other Issues', options: [
          { value: 'abortion', label: 'Abortion' }, { value: 'gun', label: 'Gun' },
          { value: 'veteran', label: 'Veteran' }, { value: 'economy', label: 'Economy' }
        ]}
      ];
    }
  },
  
  watch: {
    filters: {
      handler(newFilters) {
        this.localFilters = {
          dateRange: {
            startDate: newFilters.dateRange?.startDate || '',
            endDate: newFilters.dateRange?.endDate || ''
          },
          searchText: newFilters.searchText || '',
          selectedFactors: [...(newFilters.selectedFactors || [])]
        };
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    handleDateRangeChange() {
      if (this.selectedDateRange === 'custom') {
        // Don't emit yet, wait for user to select custom dates
        return;
      }
      
      if (this.selectedDateRange === '') {
        // Clear date range
        this.localFilters.dateRange.startDate = '';
        this.localFilters.dateRange.endDate = '';
        this.emitFilters();
        return;
      }
      
      // Calculate date range based on selected days
      const days = parseInt(this.selectedDateRange);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      // Format as YYYY-MM-DD for input
      this.localFilters.dateRange.startDate = this.formatDateForInput(startDate);
      this.localFilters.dateRange.endDate = this.formatDateForInput(endDate);
      
      this.emitFilters();
    },
    
    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    emitFilters() {
      const filters = {
        dateRange: this.localFilters.dateRange.startDate && this.localFilters.dateRange.endDate
          ? {
              startDate: this.formatDateForFilter(this.localFilters.dateRange.startDate),
              endDate: this.formatDateForFilter(this.localFilters.dateRange.endDate)
            }
          : null,
        searchText: this.localFilters.searchText,
        selectedFactors: this.localFilters.selectedFactors
      };
      
      this.$emit('update:filters', filters);
    },
    
    debouncedEmitFilters() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      
      this.debounceTimeout = setTimeout(() => {
        this.emitFilters();
      }, 250);
    },
    
    clearFilters() {
      this.localFilters = {
        dateRange: {
          startDate: '',
          endDate: ''
        },
        searchText: '',
        selectedFactors: []
      };
      this.selectedDateRange = '';
      this.emitFilters();
    },
    

    formatDateForFilter(dateStr) {
      // Convert YYYY-MM-DD (native input format) to MM-DD-YYYY (filter format)
      if (!dateStr) return '';
      
      try {
        const [year, month, day] = dateStr.split('-');
        return `${month}-${day}-${year}`;
      } catch (e) {
        console.warn('Error formatting date:', e);
        return dateStr;
      }
    },
    
    extractCategorizedFactors() {
      if (!this.protestData || this.protestData.length === 0) {
        return [];
      }
      
      // Define categories with their keywords
      const categories = [
        {
          name: 'Geopolitical / Conflict-Related',
          keywords: ['gaza', 'palestine', 'israel', 'ceasefire', 'genocide', 'war', 'peace', 'military']
        },
        {
          name: 'Immigration',
          keywords: ['immigration', 'immigrant', 'deportation', 'ice', 'border', 'refugee', 'asylum']
        },
        {
          name: 'Political / Government',
          keywords: ['trump', 'biden', 'government', 'democracy', 'voting', 'election']
        },
        {
          name: 'Climate / Environment',
          keywords: ['climate', 'environment', 'fossil', 'pipeline', 'energy', 'renewable']
        },
        {
          name: 'Healthcare',
          keywords: ['healthcare', 'health', 'medicare', 'medicaid', 'hospital']
        },
        {
          name: 'Workers / Labor',
          keywords: ['workers', 'labor', 'union', 'wage', 'jobs']
        },
        {
          name: 'Justice / Police',
          keywords: ['police', 'justice', 'prison', 'incarceration', 'reform']
        },
        {
          name: 'Rights / Equality',
          keywords: ['rights', 'civil rights', 'lgbtq', 'transgender', 'women', 'equality']
        },
        {
          name: 'Education',
          keywords: ['education', 'school', 'student', 'teacher']
        },
        {
          name: 'Housing',
          keywords: ['housing', 'rent', 'eviction', 'homeless']
        },
        {
          name: 'Other Issues',
          keywords: ['abortion', 'reproductive', 'gun', 'firearm', 'veteran', 'tax', 'economy', 'capitalism', 'socialism']
        }
      ];
      
      // Extract factors found in the data for each category
      const result = [];
      
      categories.forEach(category => {
        const foundFactors = new Set();
        
        this.protestData.forEach(feature => {
          const events = feature.properties?.events || [];
          events.forEach(event => {
            const text = `${event.event_type || ''} ${event.claims_summary || ''}`.toLowerCase();
            
            category.keywords.forEach(keyword => {
              if (text.includes(keyword)) {
                foundFactors.add(keyword);
              }
            });
          });
        });
        
        if (foundFactors.size > 0) {
          result.push({
            name: category.name,
            options: Array.from(foundFactors).sort().map(keyword => ({
              value: keyword,
              label: keyword.charAt(0).toUpperCase() + keyword.slice(1)
            }))
          });
        }
      });
      
      console.log('Extracted categorized factors:', result);
      return result;
    }
  },
  
  beforeDestroy() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }
};
</script>
